import { useEffect } from 'react';

/**
 * N2c (T4, D-126): below md a small dialog -- a form of at most three fields,
 * no rich-text editor -- is a bottom sheet sized to its content with its
 * footer pinned (the filters sheet's pattern, `mobile-bottom-sheet`); every
 * other dialog stays full screen (D-54).
 *
 * There is no shared dialog wrapper (D-54: 145 raw MUI `<Dialog>` sites), so
 * the shell classifies the open dialogs itself: each dialog root gets
 * `data-mobile-sheet` while its paper holds 1-3 fields and nothing large, and
 * the global stylesheet (`styles/_material.scss`) turns that into the sheet.
 * A dialog's form can change after it opens (an edit form reads its record),
 * so each open dialog is watched and re-classified on every change; the
 * classification runs in the observers' callbacks, not on a timer.
 */
export const SMALL_DIALOG_SHEET_ATTRIBUTE = 'data-mobile-sheet';

const MAX_FIELDS = 3;

/** A field: a form control, a labelled checkbox / switch, a slider, a segmented choice. */
const FIELD = [
  '.MuiFormControl-root',
  '.MuiFormControlLabel-root',
  '.MuiCheckbox-root',
  '.MuiSwitch-root',
  '.MuiSlider-root',
  '.MuiButtonGroup-root',
].join(', ');

/** Content that is not a small form: rich text, lists, frames, charts, tabs, a picker. */
const NOT_SMALL = [
  "[class*='html-editor_']",
  '.jodit',
  '.ace_editor',
  "[contenteditable='true']",
  '.data-grid',
  'table',
  'iframe',
  'canvas',
  '.apexcharts-canvas',
  '.MuiTabs-root',
  '.MuiDateCalendar-root',
  '.MuiPickersLayout-root',
  // already a sheet (FiltersDialog) or full screen by its own prop
  "[class*='filters-dialog_']",
  '.MuiDialog-paperFullScreen',
].join(', ');

const countFields = (paper: Element) => {
  const fields = Array.from(paper.querySelectorAll(FIELD));
  // a field inside another (the checkbox of a labelled checkbox) is one field
  return fields.filter((field) => !fields.some((other) => other !== field && other.contains(field)))
    .length;
};

const isSmallDialog = (root: Element) => {
  const paper = root.querySelector('.MuiDialog-paper');
  if (!paper || paper.matches(NOT_SMALL) || paper.querySelector(NOT_SMALL)) {
    return false;
  }
  const count = countFields(paper);
  return count > 0 && count <= MAX_FIELDS;
};

const classify = (root: Element) => {
  const isSmall = isSmallDialog(root);
  if (isSmall !== root.hasAttribute(SMALL_DIALOG_SHEET_ATTRIBUTE)) {
    root.toggleAttribute(SMALL_DIALOG_SHEET_ATTRIBUTE, isSmall);
  }
};

/** Mounted once by the app shell; `enabled` is the shell's below-md flag. */
export const useSmallDialogSheets = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const watched = new Map<Element, MutationObserver>();
    const update = () => {
      document.querySelectorAll('.MuiDialog-root').forEach((root) => {
        classify(root);
        if (!watched.has(root)) {
          const observer = new MutationObserver(() => classify(root));
          observer.observe(root, { childList: true, subtree: true });
          watched.set(root, observer);
        }
      });
      watched.forEach((observer, root) => {
        if (!root.isConnected) {
          observer.disconnect();
          watched.delete(root);
        }
      });
    };
    // MUI portals every dialog into <body>
    const portals = new MutationObserver(update);
    portals.observe(document.body, { childList: true });
    update();
    return () => {
      portals.disconnect();
      watched.forEach((observer) => observer.disconnect());
      document
        .querySelectorAll(`[${SMALL_DIALOG_SHEET_ATTRIBUTE}]`)
        .forEach((root) => root.removeAttribute(SMALL_DIALOG_SHEET_ATTRIBUTE));
    };
  }, [enabled]);
};
